import { Component, Prop, h } from '@stencil/core';

/**
 * Dynamic content component for SignNow brand
 *
 * @product signnow
 * @source dynamiccontent
 * @version 1.0.1
 * @category content
 * @contentConfig Simple wrapper component for dynamic HTML content from backend
 * @prop {object} values - {title, text} - Object containing title and HTML text content for the component
 */

@Component({
  tag: 'sn-dynamic-content',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNDynamicContent {
  /**
   * Title for the dynamic content section
   */
  @Prop({ attribute: 'title', reflect: true }) heading?: string;

  /**
   * HTML text content to be displayed
   */
  @Prop({ attribute: 'text', reflect: true }) text?: string;

  /**
   * @dynamic
   * @type {object}
   * @property {string} title - Title for the dynamic content section
   * @property {string} text - HTML text content to be displayed
   * @property {string} raw_html - Raw HTML content (optimized for SSR, bypasses innerHTML parsing)
   */
  @Prop() values?: {
    title?: string;
    text?: string;
    raw_html?: string;
  };

  render() {
    let contentValues = this.values;

    if (typeof contentValues === 'string') {
      try {
        contentValues = JSON.parse(contentValues);
      } catch (e) {
        console.error('Failed to parse values prop:', e);
      }
    }

    // Use individual props if available, otherwise fall back to values object
    const titleContent = this.heading ?? contentValues?.title;
    // Prefer raw_html over text for better SSR performance (bypasses innerHTML parsing)
    const textContent = this.text ?? contentValues?.raw_html ?? contentValues?.text;

    return (
      <me-section yGap="2xl" yGapLg="5xl" role="region" aria-label="Dynamic Content">
        <me-row>
          <me-column span="12" spanMd="8">
            {titleContent && (
              <me-heading variant="2" class="me-mb-xl">
                {titleContent}
              </me-heading>
            )}
            {textContent && <div class="sn-dynamic-content" innerHTML={textContent}></div>}
          </me-column>
        </me-row>
      </me-section>
    );
  }
}
